import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from "./auth.service";
import { CalmeanAuthService } from "./calmean-auth.service";
import moment, { Moment } from "moment";
import { co } from "@fullcalendar/core/internal-common";

// Interfaces for data models
export interface Device {
    id: string;
    name: string;
    type: string;
    status?: string;
    serialNumber?: string;
    macAddress?: string;
    description?: string;
    lastSeen?: string;
    batteryLevel?: number;
    location?: string;
    owner?: string;
    model?: string;
    osVersion?: string;
    lastUpdateDate?: string;
    purchaseDate?: string;
    warrantyEnd?: string;
    notes?: string;
}

export interface SimInfo {
    iccid: string;
    msisdn?: string;
    status?: string;
    provider?: string;
    activationDate?: string;
    expirationDate?: string;
}

export interface License {
    id: string;
    type: string;
    status: string;
    validFrom?: string;
    validTo?: string;
    components?: LicenseComponent[];
}

export interface LicenseComponent {
    id: string;
    name: string;
    type: string;
    validFrom?: string;
    validTo?: string;
}

export interface PaymentChannel {
    id: string;
    name: string;
    description?: string;
}

export interface PaymentMethodGroup {
    id: string;
    name: string;
    userGroupId?: string;
    productGroupId?: string;
    paymentChannelId?: string;
}

export interface PaymentMethod {
    id: string;
    name: string;
    paymentMethodGroupId: string;
    description?: string;
}

export interface SimInfoTMobile {
    deviceSimStatus: string;
    deviceSimStatusDescription: string;
    deviceSimStatusColor: string;
    status: string;
    simInfo: {
        dataSessionZone: string;
        dataSessionOperator: string;
        planName: string;
        planValidDate: string|null;
        voiceBalanceSeconds: number;
        smsBalance: number;
        dataBalanceKBytes: number;
        dataBalanceKBytesEu: number;
        voiceCounterSeconds: number;
        smsCounter: string;
        dataCounterKBytes: number;
        voiceLimitSeconds: number;
        smsLimit: string;
        balances: SimInfoBalance[];
        imei: string;
        iccid: string;
        dataLimitKBytes: number;
        dataLimitKBytesEu: number;
        voiceAdditionalBalanceSeconds: number;
        smsAdditionalBalance: number;
        dataAdditionalBalanceKBytes: number;
        dataAdditionalBalanceMBytes: number;
        dataAdditionalBalanceKBytesEu: number;
        dataAdditionalBalanceMBytesEu: number;
        voiceAdditionalPlanBalanceSeconds: number;
        smsAdditionalPlanBalance: number;
        dataAdditionalPlanBalanceKBytes: number;
        dataAdditionalPlanBalanceKBytesEu: number;
        planVoiceBalanceSeconds: number;
        planSmsBalance: number;
        planDataBalanceKBytes: number;
        planDataBalanceKBytesEu: number;
        roamingStatus: string;
        planRoaming: boolean;
        msisdn: string;
        dataSessionStart: string|null;
        dataSessionEnd: string|null;
        deviceSimStatusColor: 'black';
    }
}

export interface SimInfoBalance {
    zone: 'TOTAL' | 'EU' | 'NON_EU' | string;
    voiceBalanceSeconds: number;
    smsBalance: number;
    dataBalanceKBytes: number;
    voiceCounterSeconds: number;
    smsCounter: number;
    dataCounterKBytes: number;
    voiceLimitSeconds: number;
    smsLimit: number;
    dataLimitKBytes: number;
}

export interface LicenseStatus {
    licenseStatus: string;
    status: string;
    licenses: [{
        length: string;
        licenseUuid: string;
        accountUuid: string;
        licenseTypeName: string;
        validFrom: string;
        validTo: string;
    }]
}

export interface DeviceProperty {
    code: string;
    name: string;
    value: boolean;
}

export interface DevicePropertiesResponse {
    status: string;
    properties: DeviceProperty[];
}

export interface Statistics {
    content: string;
    value: string;
    type: string;
    receivedTime: string; // ISO date string, e.g., "2025-06-30T04:06:12+0200"
    createTime: string;
    properties: {
        localization_name: string;
        type: string;
        latitude: string;
        longitude: string;
    }// ISO date string, e.g., "2025-06-30T04:06:07+0200"
}

export interface Zone {
    enabled: boolean;
    name: string;
    formattedTime: string;
    formattedDays: string; // ISO date string, e.g., "2025-06-30T04:06:12+0200"
    isSilent: boolean;
    alerts: string;
    leave: boolean;
    type: string;
    geofence: {
        radius: string;
        latitude: number;
        longitude: number;
    }// ISO date string, e.g., "2025-06-30T04:06:07+0200"
}

export interface ZoneMarker {
    id: number;
    name: string;
    icon: string;
    coords: {
        lat: number;
        lng: number;
    };
    radius: number;
}

/**
 * Service for interacting with Calmean API
 */
@Injectable({
    providedIn: 'root'
})
export class CalmeanDataService {
    // Added for Change SIM migration from AngularJS
    /**
     * Replace device SIM (legacy endpoint: replaceDeviceSim)
     */
    replaceDeviceSim(args: { shortCode?: string; iccid?: string; simSupport?: string; operator?: string; msisdn?: string; registrationCode?: string; }): Observable<any> {
        const methodName = 'replaceDeviceSim';
        const url = this.getCallUrl(methodName);
        const params: any = {
            shortCode: args.shortCode,
            iccid: args.iccid,
            simSupport: args.simSupport,
            operator: args.operator,
            msisdn: args.msisdn,
            registrationCode: args.registrationCode
        };
        const options = { headers: this.getAuthHeaders(), params: this.createHttpParams(params) };
        return this.http.get<any>(url, options).pipe(
            map(result => result),
            catchError(this.handleError)
        );
    }

    replaceAccounts(email1: string, email2: string): Observable<any> {
        const methodName = "replaceAccounts";
        const url = this.getCallUrl(methodName);
        var params = {
            email1: email1,
            email2: email2
        };

        const config = this.getCallConfig(params);

        console.log(url, params, config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    replaceDevice(sourceRegistrationCode: string, destinationRegistrationCode?: string, destinationShortCode?: string, activateSim?: string, accountScope?: boolean): Observable<any> {
        const methodName = "replaceDevice";
        const url = this.getCallUrl(methodName);
        let params = {
            sourceRegistrationCode: sourceRegistrationCode,
            destinationRegistrationCode: destinationRegistrationCode
        };

        if (destinationShortCode) {
            params = Object.assign(params, { destinationShortCode });
        }

        if (activateSim) {
            params = Object.assign(params, { activateSim });
        }

        if (accountScope) {
            params = Object.assign(params, { accountScope });
        }

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    paymentStatusRequest(accountUuid: string, deviceId?: string): Observable<any> {
        const methodName = "paymentStatusRequest";
        const url = this.getCallUrl(methodName);
        let params = {
            accountUuid: accountUuid,
        };

        if (deviceId) {
            params = Object.assign(params, { deviceId });
        }

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Replace device id (legacy endpoint: replaceDevice)
     */
    // replaceDevice(registrationCode?: string, key?: string, scanner?: string): Observable<any> {
    //     const methodName = 'replaceDevice';
    //     const url = this.getCallUrl(methodName);
    //     const params: any = {
    //         registrationCode: registrationCode,
    //         key: key,
    //         scanner: scanner
    //     };
    //     const options = { headers: this.getAuthHeaders(), params: this.createHttpParams(params) };
    //     return this.http.get<any>(url, options).pipe(
    //         map(result => result),
    //         catchError(this.handleError)
    //     );
    // }
    // Store token
    token: string | null = null;

    access = {
        code: "",
        visible: false,
        time: 900,
        timer: 0
    };
    /*LOCATIONS*/
    locations = {
        lastMarkers: [] as any[],
        locations: [] as any[],
        polyLines: [] as any[],
        circles: [] as any[],
        zones: [] as any[],
        zonesMarkers: [] as any[],
        defaultZoom: 15,
        center: {
            lat: 52.233333,
            lng: 21.016667
        }, control: {}
    }

    emptyCircle = {
        id: 0,
        visible: true,
        editable: false,
        draggable: false,
        geodesic: true,
        stroke: {
            color: '#f0f0f0',
            weight: 1
        },
        fill: {
            color: '#0A3C6E',
            opacity: 0.2
        },
        center: {
            latitude: 0,
            longitude: 0
        }
    };

    emptyPolyLine = {
        id: null,
        visible: true,
        geodesic: true,
        editable: false,
        draggable: false,
        options: {},
        path: [],
        icons: [
            {
                icon: {
                    path: 4
                },
                offset: '5px',
                repeat: '35px'
            }],
        stroke: {
            color: '#0A3C6E',
            weight: 1
        }
    };

    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private calmeanAuthService: CalmeanAuthService
    ) {
        this.initialize()
    }

    /**
     * Check if token is active
     * @returns boolean indicating if token is active
     */
    activeToken(): boolean {
        return !!this.token;
    }

    /**
     * Logout from the system
     * Clears token from memory and sessionStorage
     */
    logout(): void {
        console.log('Logging out, clearing token');
        this.token = null;
        this.removeSessionData('calmeanToken');
        this.removeSessionData('accountToken');
    }

    /**
     * Get the current token
     * @returns The current token or null if not logged in
     */
    getToken(): string | null {
        return this.token;
    }

    /**
     * Initialize the service
     * Checks for existing token in sessionStorage and sets it if available
     */
    initialize(): void {
        console.log('CalmeanDataService initializing');

        // Check for existing token in sessionStorage
        const storedToken = this.getSessionData<string>('calmeanToken');
        if (storedToken) {
            console.log('Found existing token in sessionStorage');
            this.token = storedToken;
        } else {
            console.log('No existing token found in sessionStorage');
            this.token = null;
        }

        console.log('CalmeanDataService initialized');
    }

    // /**
    //  * Login to the system
    //  * @param username Username
    //  * @param password Password
    //  * @returns Observable of login response
    //  */
    // login(username: string, password: string): Observable<any> {
    //   const url = this.getCallUrl('login', 'license');
    //   const params = {
    //     username: username,
    //     password: password
    //   };
    //
    //   console.log("login", url, params);
    //
    //   return this.http.get(url, { params: this.createHttpParams(params) })
    //     .pipe(
    //       map(result => {
    //         // Store token in memory and sessionStorage
    //         if (result && result.token) {
    //           this.token = result.token;
    //           this.setSessionData('calmeanToken', result.token);
    //           console.log('Token stored in sessionStorage');
    //         }
    //         return result;
    //       }),
    //       catchError(this.handleError)
    //     );
    // }

    createCircle(ctx: any, x: number, y: number, radius: number, color?: string) {
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.closePath();
        ctx.fill();
        ctx.closePath();
    }

    createCanvasLastMarker(backgroundColor: string) {

        let canvas = document.createElement("canvas");
        canvas.width = 60;
        canvas.height = 65;
        let ctx = canvas.getContext("2d");
        if (!ctx) {
            // Context could not be obtained; return a fallback value
            return "";
        }

        //creating circle border outside
        this.createCircle(ctx, 33, 36, 22, 'white');

        //creating triangle border
        ctx.beginPath();
        ctx.moveTo(12, 43);
        ctx.lineTo(30, 65);
        ctx.lineTo(48, 43);
        ctx.fillStyle = 'white';
        ctx?.closePath();
        ctx?.fill();

        //creating circle
        this.createCircle(ctx, 30, 33, 20, backgroundColor);

        //creating circle
        ctx.beginPath();
        ctx.moveTo(14, 43);
        ctx.lineTo(30, 63);
        ctx.lineTo(46, 43);
        ctx.fillStyle = backgroundColor;
        ctx.closePath();
        ctx.fill();

        //creating circle border inside
        // createCircle(ctx, 30, 33, 7, 'white');

        //creating circle 'hole' inside
        // ctx.beginPath();
        // ctx.globalCompositeOperation = 'destination-out';
        // ctx.arc(30, 33, 5, 0, 2 * Math.PI, true);
        // ctx.fill();
        // ctx.closePath();

        return canvas.toDataURL();
    }

    createCanvasZoneMarker(zoneName: string) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            // Context could not be obtained; return a fallback value
            return "";
        }

        ctx.font = "20px Roboto";
        ctx.fillStyle = '#2fa330';

        canvas.width = ctx.measureText(zoneName).width + 2;
        canvas.height = 20;

        ctx.font = "18px Roboto";
        ctx.fillStyle = '#2fa330';
        ctx.fillText(zoneName, 1, 20);

        return canvas.toDataURL();
    }

    getLocationFormattedTime(location: any) {
        const from = moment([2000]).add(location.monitorTime.dayTimePeriodSecondsFrom, 'seconds');
        const to = moment([2000]).add(location.monitorTime.dayTimePeriodSecondsTo, 'seconds');

        return from.format('HH:mm') + ' - ' + to.format('HH:mm');
    }

    getLocationFormattedDays(location: any) {
        const dayTable = {
            1: 'Pon',
            2: 'Wt',
            4: 'Śr',
            8: 'Czw',
            16: 'Pią',
            32: 'Sob',
            64: 'Nie'
        };

        const result: string[] = [];

        Object.entries(dayTable).forEach(([key, val]) => {
            if (location.monitorTime.weekDay & Number(key)) {
                result.push(val);
            }
        });

        return result.join(', ');
    }

    createCanvasMarker(backgroundColor: any) {
        let canvas = document.createElement("canvas");
        canvas.width = 40;
        canvas.height = 45;
        let ctx = canvas.getContext("2d");
        if (!ctx) {
            // Context could not be obtained; return a fallback value
            return "";
        }
        //creating circle border
        this.createCircle(ctx, 20, 23, 15, 'white');

        //creating triangle border
        ctx.beginPath();
        ctx.moveTo(8, 23);
        ctx.lineTo(20, 45);
        ctx.lineTo(32, 23);
        ctx.fillStyle = 'white';
        ctx.closePath();
        ctx.fill();

        //creating circle
        this.createCircle(ctx, 20, 23, 13, backgroundColor);

        //creating triangle
        ctx.beginPath();
        ctx.moveTo(10, 23);
        ctx.lineTo(20, 43);
        ctx.lineTo(30, 23);
        ctx.fillStyle = backgroundColor;
        ctx.closePath();
        ctx.fill();

        return canvas.toDataURL();
    }

    getSecondsFormattedTime(secondsFrom: any, secondsTo: any) {
        let from = moment([2000]).add(secondsFrom, 'seconds');
        let to = moment([2000]).add(secondsTo, 'seconds');

        return from.format('HH:mm') + ' - ' + to.format('HH:mm');
    }


    clearDataLocations() {
        this.locations.locations.length = 0;
        this.locations.lastMarkers.length = 0;
        this.locations.circles.length = 0;
        this.locations.polyLines.length = 0;
    }

    parsingLocationForMap(newLocations: any[]) {

        this.clearDataLocations();

        let newLocationsLength = newLocations.length,
            newLocationCounter;

        for (newLocationCounter = 0; newLocationCounter < newLocationsLength; newLocationCounter++) {
            let alreadyExist = false;
            let newLocation = newLocations[newLocationCounter];
            // console.log("newLocation %s", angular.toJson(newLocation))

            if (this.locations.lastMarkers.length > 0) {
                var lastLatLang = new google.maps.LatLng(newLocation.latitude, newLocation.longitude);
                var currentLatLang = new google.maps.LatLng(this.locations.lastMarkers[0].latitude, this.locations.lastMarkers[0].longitude);
                var distance = google.maps.geometry.spherical.computeDistanceBetween(lastLatLang, currentLatLang);
                if (distance === 0 || distance < 5) {
                    continue;
                }
            }

            for (var locationCounter = 0; locationCounter < this.locations.locations.length; locationCounter++) {
                alreadyExist = false;
                //Checking if location already exists
                if (newLocation.latitude === this.locations.locations[locationCounter].latitude &&
                    newLocation.longitude === this.locations.locations[locationCounter].longitude &&
                    newLocation.createTime === this.locations.locations[locationCounter].createTime
                ) {
                    alreadyExist = true;
                    break;
                }
            }
            if (this.locations.lastMarkers.length > 0 &&
                newLocation.latitude === this.locations.lastMarkers[0].latitude &&
                newLocation.longitude === this.locations.lastMarkers[0].longitude &&
                newLocation.createTime === this.locations.lastMarkers[0].createTime
            ) {
                alreadyExist = true;
            }

            if (alreadyExist) {
                continue;
            }

            if (this.locations.lastMarkers.length > 0 && newLocationCounter >= 1 && newLocationCounter < newLocations.length - 1) {
                let smallDistance = this.checkDistance(newLocation, newLocations[newLocationCounter - 1]);
                if (smallDistance)
                    continue;
            }

            newLocation.coords = {
                lat: newLocation.latitude,
                lng: newLocation.longitude
            };

            newLocation.label = {
                text: moment(newLocation.createTime).format('HH:mm'),
                color: 'white',
                fontSize: '10px',
                fontWeight: 'bold'
            };

            newLocation.lastMarker = false;

            if (newLocation.type === "LBS") {

                var newCircle = this.createCircleForMap(newLocationCounter, newLocations, newLocation);
                this.locations.circles.push(newCircle);

                if (this.locations.locations.length === 0 ||
                    moment(newLocation.createTime).valueOf() <= moment(this.locations.locations[this.locations.locations.length - 1].createTime).valueOf() ||
                    moment(newLocation.createTime).valueOf() >= moment(this.locations.lastMarkers[0].createTime).valueOf()
                ) {
                    newLocation.icon = this.createCanvasMarker('#b4b4b4');
                } else continue;
            } else {
                newLocation.icon = this.createCanvasMarker('#ffa000');
            }


            if (this.locations.lastMarkers.length > 0 && moment(newLocation.createTime).valueOf() >= moment(this.locations.lastMarkers[0].createTime).valueOf()) {

                if (this.locations.lastMarkers[0].type !== "LBS")
                    this.locations.lastMarkers[0].icon = this.createCanvasMarker('#ffa000');
                else
                    this.locations.lastMarkers[0].icon = this.createCanvasMarker('#b4b4b4');

                this.locations.lastMarkers[0].lastMarker = false;
                this.locations.locations.push(this.locations.lastMarkers[0]);
                this.locations.lastMarkers.pop();

                if (newLocation.type !== "LBS") {
                    newLocation.icon = this.createCanvasLastMarker('#ffa000');
                    newLocation.lastMarker = true;
                } else {
                    newLocation.icon = this.createCanvasLastMarker('#b4b4b4');
                    newLocation.lastMarker = true;
                }
                this.locations.lastMarkers.push(newLocation);
            } else if (this.locations.lastMarkers.length === 0) {

                if (newLocation.type !== "LBS") {
                    newLocation.icon = this.createCanvasLastMarker('#ffa000');
                    newLocation.lastMarker = true;
                } else {
                    newLocation.icon = this.createCanvasLastMarker('#b4b4b4');
                    newLocation.lastMarker = true;
                }

                this.locations.lastMarkers.push(newLocation);
            } else {
                this.locations.locations.push(newLocation);
            }

            var currentPolyLine = JSON.parse(JSON.stringify(this.emptyPolyLine));
            currentPolyLine.id = this.locations.polyLines.length;

            if (this.locations.polyLines.length === 0) {
                this.locations.polyLines[0] = JSON.parse(JSON.stringify(this.emptyPolyLine));
                this.locations.polyLines[0].id = 0;
            }

            currentPolyLine.path = {
                lat: newLocation.latitude,
                lng: newLocation.longitude
            };

            var isItExistingPath = false;

            for (var pathCounter = 0; pathCounter < this.locations.polyLines[0].path.length; pathCounter++) {
                if (this.locations.polyLines[0].path[pathCounter].lat === currentPolyLine.path.lat &&
                    this.locations.polyLines[0].path[pathCounter].lng === currentPolyLine.path.lng) {
                    isItExistingPath = true;
                    break;
                }
            }

            if (!isItExistingPath && newLocation.lastMarker === false)
                this.locations.polyLines[0].path.push(currentPolyLine.path);
            else if (newLocation.lastMarker === true)
                this.locations.polyLines[0].path.unshift(currentPolyLine.path);

            this.locations.polyLines[0].options = {
                strokeColor: '#0A3C6E',
                strokeWeight: 2,
                icons: [
                    {
                        icon: {
                            path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                            scale: 2,
                            strokeColor: '#0A3C6E',
                            fillColor: '#0A3C6E',
                            fillOpacity: 1
                        },
                        offset: '5px',
                        repeat: '35px'
                    }
                ]
            };
        }
        this.infoInLBS();
        this.addIds();

    }

    infoInLBS() {
        if (!this.locations.lastMarkers[0]) {
            return;
        }
        this.locations.locations.forEach((information, index) => {
            if (information.type === "LBS")
                this.locations.locations[index].info = "W odległości " + this.locations.locations[index].radius + " metrów od:";
        });

        if (this.locations.lastMarkers[0].type === "LBS") {
            this.locations.lastMarkers[0].info = "W odległości " + this.locations.lastMarkers[0].radius + " metrów od:";
        }
    }

    addIds() {
        if (!this.locations.lastMarkers[0]) {
            return;
        }
        this.locations.lastMarkers[0].id = 0;
        for (var locationCounter = 0; locationCounter < this.locations.locations.length; ++locationCounter) {
            this.locations.locations[locationCounter].id = locationCounter + 1;
        }
    }

    createCircleForMap(howMany: any, allLocations: any, location: any) {
        var newCircle = JSON.parse(JSON.stringify(this.emptyCircle));
        newCircle.id = this.locations.circles.length;
        newCircle.center = location.coords;
        newCircle.radius = parseInt(allLocations[howMany].radius);

        return newCircle;
    }

    checkDistance(lastLoc: any, currentLoc: any) {
        let lastLatLang = new google.maps.LatLng(lastLoc.latitude, lastLoc.longitude);
        let currentLatLang = new google.maps.LatLng(currentLoc.latitude, currentLoc.longitude);

        let distance = google.maps.geometry.spherical.computeDistanceBetween(lastLatLang, currentLatLang);

        return distance === 0 || distance < 5;
    }

    getLocationFormattedDaysForWeekDay(weekDay: any) {
        let dayTable = {
            1: 'Pon',
            2: 'Wt',
            4: 'Śr',
            8: 'Czw',
            16: 'Pią',
            32: 'Sob',
            64: 'Nie'
        };

        const result: string[] = [];

        Object.entries(dayTable).forEach(([key, val]) => {
            if (weekDay & Number(key)) {
                result.push(val);
            }
        });

        return result.join(', ');
    }

    /**
     * Get T-Mobile SIM information
     * @param msisdn
     * @param deviceUuid
     */
    getTMobileSimInfo(msisdn: string, deviceUuid: string): Observable<any> {
        const methodName = 'getTMobileSimInfo';
        const url = this.getCallUrl(methodName);
        const params = {
            msisdn: msisdn,
            deviceUuid: deviceUuid
        };

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get devices
     * @param filter Filter parameters
     * @returns Observable of Device array
     */
    getDevices(filter?: any): Observable<Device[]> {
        const methodName = 'getDevices';
        const url = this.getCallUrl(methodName, 'license');
        const params = filter || {};

        console.log(methodName, url, params);

        return this.http.get<Device[]>(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get a specific device
     * @param deviceId Device ID
     * @returns Observable of Device
     */
    getDevice(deviceId: string): Observable<Device> {
        const methodName = 'getDevice';
        const url = this.getCallUrl(methodName, 'license');
        let params = { deviceId };

        console.log(methodName, url, params);

        return this.http.get<Device>(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get printing devices
     */
    getPrintingDevices(): Observable<any> {
        const methodName = 'getPrintingDevices';
        const url = this.getCallUrl(methodName, 'license');
        let params = {};

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get SIM information
     * @param iccid ICCID
     * @returns Observable of SimInfo
     */
    getSimInfo(iccid: string): Observable<SimInfo> {
        const methodName = 'getSimInfo';
        const url = this.getCallUrl(methodName, 'license');
        let params = { iccid };

        console.log(methodName, url, params);

        return this.http.get<SimInfo>(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getSMSGwHints(deviceUuid?: string | null): Observable<any> {
        const methodName = 'getSMSGwHints';
        const url = this.getCallUrl(methodName);

        let params = {
            deviceUuid: deviceUuid
        };

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    sendSMSGwMessage(recipient: string, content: string, sender: string): Observable<any> {
        const methodName = 'sendSMSGwMessage';
        const url = this.getCallUrl(methodName);

        let params = {
            recipient: recipient,
            content: content
        };
        if (sender) {
            params = Object.assign(params, { sender });
        }

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getSMSGwData(msisdn: string, blockToken?: string): Observable<any> {
        const methodName = 'getSMSGwData';
        const url = this.getCallUrl(methodName);

        let params = {
            msisdn: msisdn
        };

        if (blockToken) {
            params = Object.assign(params, { blockToken });
        }

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    updateConfigurationSim(iccid: string, name: string): Observable<any> {
        const methodName = "updateConfigurationSim";
        const url = this.getCallUrl(methodName);
        let params = {
            iccid: iccid,
            name: name
        };

        const config = this.getCallConfig(params, "application/json");

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    createCalmeanStarter(iccid: string, partner: string): Observable<any> {
        const methodName = "createCalmeanStarter";
        const url = this.getCallUrl(methodName);
        let params = {
            iccid: iccid,
            partner: partner
        };

        const config = this.getCallConfig(params, "application/json");

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getSimShipping(mailcode: string): Observable<any> {
        const methodName = "getSimShipping";
        const url = this.getCallUrl(methodName);
        let params = {
            "simShippingId": mailcode
        };

        const config = this.getCallConfig(params);

        console.log(url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    addSimShippingIccid(simShippingId: string, deviceUuid: string, iccid?: string): Observable<any> {
        const methodName = "addSimShippingIccid";
        const url = this.getCallUrl(methodName);
        let params = {
            "simShippingId": simShippingId,
            "deviceUuid": deviceUuid,
            "iccid": iccid
        };

        const config = this.getCallConfig(params);

        console.log(url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    deleteSimShipping(uuid: string): Observable<any> {
        const methodName = "deleteSimShipping";
        const url = this.getCallUrl(methodName);
        let params = {
            "accountUuid": uuid
        };

        const config = this.getCallConfig(params);

        console.log(url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );

    }

    getSimShippingView(page: number, size: number, accountEmail?: string, registrationCode?: string, surname?: string): Observable<any> {
        const methodName = "getSimShippingView";
        const url = this.getCallUrl(methodName);
        let params = {
            "page": page,
            "size": size,
        };

        if (accountEmail && accountEmail.length > 0) {
            params = Object.assign(params, { accountEmail });
        }

        if (registrationCode && registrationCode.length > 0) {
            params = Object.assign(params, { registrationCode });
        }

        if (surname && surname.length > 0) {
            params = Object.assign(params, { surname });
        }

        const config = this.getCallConfig(params);

        console.log(url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    updateSimShippingView(pack: any): Observable<any> {
        const methodName = "updateSimShipping";
        const url = this.getCallUrl(methodName);
        const params = this.removeEmptyParams({
            accountUuid: pack.accountUuid,
            name: pack.name,
            surname: pack.surname,
            street: pack.street,
            city: pack.city,
            postalCode: pack.postalCode,
            country: pack.country,
            companyName: pack.companyName
        });

        const config = this.getCallConfig(params);

        console.log(url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    updateSimShipmentPackAddress(addressPack: any): Observable<any> {
        const methodName = "updateSimShipmentPackAddress";
        const url = this.getCallUrl(methodName);
        const params = {};
        const config = this.getCallConfig(params);

        console.log(url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, addressPack, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    private removeEmptyParams(params: Record<string, any>): Record<string, any> {
        return Object.entries(params).reduce((result, [key, value]) => {
            if (value !== undefined && value !== null) {
                result[key] = value;
            }
            return result;
        }, {} as Record<string, any>);
    }

    uploadInactiveMsisdnPdf(fd: FormData, type?: string): Observable<any> {
        console.log('upload content');
        const methodName = 'uploadInactiveMsisdnPdf';
        let params = {}

        const url = this.getCallUrl(methodName);
        const config = this.getCallConfig(params, type);
        console.log('upload content', config);

        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        // When sending FormData, do NOT set the Content-Type header manually.
        // The browser must set it (with proper multipart boundary), otherwise the file won't be received.
        if (httpOptions.headers) {
            // headers here is a plain object, not Angular HttpHeaders
            const headers = { ...(httpOptions.headers as any) };
            delete headers['Content-Type'];
            httpOptions.headers = headers;
        }

        return this.http.post(url, fd, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    clearTMobileSimMigrationStatusList(): Observable<any> {
        const methodName = 'clearTMobileSimMigrationStatusList';
        const url = this.getCallUrl(methodName);
        let params = {};

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getTMobileSimMigrationReadyCount(): Observable<any> {
        const methodName = 'getTMobileSimMigrationReadyCount';
        const url = this.getCallUrl(methodName);
        let params = {};
        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getTMobileSimMigrationStatusList(): Observable<any> {
        const methodName = 'getTMobileSimMigrationStatusList';
        const url = this.getCallUrl(methodName);
        let params = {};
        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    addTMobileSimMigration(iccid: string, msisdn: string): Observable<any> {

        const methodName = 'addTMobileSimMigration';
        const url = this.getCallUrl(methodName);
        let params = {
            'iccid': iccid,
            'msisdn': msisdn
        }
        const config = this.getCallConfig(params, "application/json"); //  'application/x-www-form-urlencoded;charset=UTF-8'

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    addTMobileSimMigrationIccid(iccid: string[], roamingEnabled: boolean): Observable<any> {

        const methodName = 'addTMobileSimMigrationIccid';
        let url = this.getCallUrl(methodName);
        url += '?roamingEnabled=' + roamingEnabled
        const params = {
            'roamingEnabled': roamingEnabled,
        }
        const config = this.getCallConfig(params, "application/json"); //  'application/x-www-form-urlencoded;charset=UTF-8'

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, iccid, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    configureDevice(scanRegistrationCode: boolean, configurationType: string, deviceType: string, deviceColor: string, languageSupport: string,
        activeSimIccid: string, series: string, roaming: boolean, inactiveSimIccid?: string, registrationCode?: string, imei?: string): Observable<any> {
        const methodName = "configureDevice";
        const url = this.getCallUrl(methodName);
        let params = {
            scanRegistrationCode: scanRegistrationCode,
            configurationType: configurationType,
            deviceType: deviceType,
            deviceColor: deviceColor,
            languageSupport: languageSupport,
            activeSimIccid: activeSimIccid,
            series: series,
            roaming: roaming
        };

        if (inactiveSimIccid) {
            params = Object.assign(params, { inactiveSimIccid });
        }

        if (registrationCode) {
            params = Object.assign(params, { registrationCode });
        }

        if (imei) {
            params = Object.assign(params, { imei });
        }

        const config = this.getCallConfig(params, "application/json");

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    updateSimPlan(msisdn?: string, planValidDate?: number): Observable<any> {
        const methodName = 'updateSimPlan';
        const url = this.getCallUrl(methodName);

        let params = {
            msisdn: msisdn,
            planValidToMs: planValidDate
        };

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    setSimAdditionalBalance(msisdn: string, voiceSeconds: number, sms: number, dataKb: number, dataAdditionalBalanceKBytesEu: number,
        planVoiceBalanceSeconds: number, voiceAdditionalPlanBalanceSeconds: number,
        planSmsBalance: number, smsAdditionalPlanBalance: number, planDataBalanceKBytes: number,
        dataAdditionalPlanBalanceKBytes: number, planDataBalanceKBytesEu: number,
        dataAdditionalPlanBalanceKBytesEu: number): Observable<any> {
        const methodName = 'updateSimBalance';
        const url = this.getCallUrl(methodName);

        let params = {
            msisdn: msisdn,
            voiceAdditionalBalanceSeconds: voiceSeconds,
            smsAdditionalBalance: sms,
            dataAdditionalBalanceKBytes: dataKb,
            dataAdditionalBalanceKBytesEu: dataAdditionalBalanceKBytesEu,
            planVoiceBalanceSeconds: planVoiceBalanceSeconds,
            voiceAdditionalPlanBalanceSeconds: voiceAdditionalPlanBalanceSeconds,
            planSmsBalance: planSmsBalance,
            smsAdditionalPlanBalance: smsAdditionalPlanBalance,
            planDataBalanceKBytes: planDataBalanceKBytes,
            dataAdditionalPlanBalanceKBytes: dataAdditionalPlanBalanceKBytes,
            planDataBalanceKBytesEu: planDataBalanceKBytesEu,
            dataAdditionalPlanBalanceKBytesEu: dataAdditionalPlanBalanceKBytesEu
        };

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    expireSim(iccid: string): Observable<any> {
        const methodName = 'expireSim';
        const url = this.getCallUrl(methodName);

        let params = {
            iccid: iccid,
        };

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getSimData(msisdn: string, iccid: string): Observable<any> {
        const methodName = 'getSimData';
        const url = this.getCallUrl(methodName);
        let params = {
            msisdn: msisdn,
            iccid: iccid
        };

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getSimDataDevices(msisdn: string, iccid: string, imei: string): Observable<any> {
        const methodName = 'getSimDataDevices';
        const url = this.getCallUrl(methodName);
        let params = {
        };

        if (msisdn) {
            params = Object.assign(params, { msisdn });
        }

        if (iccid) {
            params = Object.assign(params, { iccid });
        }

        if (imei) {
            params = Object.assign(params, { imei });
        }

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    fullfilment(order_id: string, reg_code: string, no_number: number) {
        const methodName = "fullfilment/" + order_id + "/" + reg_code + "/" + no_number;
        const context = "bobaselinker";
        const url = this.getCallUrl(methodName, context, true);
        var params = {};

        const config = this.getCallConfig(params, "application/json");

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getAllOrders(status: string) {
        const methodName = "orders/" + status;
        const context = "bobaselinker";
        const url = this.getCallUrl(methodName, context, true);
        var params = {};

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getBLStatuses() {
        const methodName = "statuses";
        const context = "bobaselinker";
        const url = this.getCallUrl(methodName, context, true);
        var params = {};

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    searchBLOrders(waybill: string, status: number) {
        const methodName = "order/" + waybill + "/" + status;
        const context = "bobaselinker";
        const url = this.getCallUrl(methodName, context, true);
        var params = {};

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    validateRegCode(reg_code: string) {
        const methodName = "validateregcode/" + reg_code;
        const context = "bobaselinker";
        const url = this.getCallUrl(methodName, context, true);
        var params = {};

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    saveBLOrder(order: string) {
        const methodName = "updatebo";
        const context = "bobaselinker";
        const url = this.getCallUrl(methodName, context, true);
        var params = {};

        const config = this.getCallConfig(params, "application/json");

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, order, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    updateProductOrder(orderId: string, registrationCode: string, accountCheckEnabled: boolean) {
        const methodName = 'updateProductOrder';
        const url = this.getCallUrl(methodName);
        if (accountCheckEnabled === undefined) {
            accountCheckEnabled = false;
        }
        var params = {
            orderId: orderId,
            registrationCode: registrationCode,
            accountCheckEnabled: accountCheckEnabled
        };
        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * @name CalmeanDataService.updateDevice
     */
    updateDevice(deviceUUID: string, deviceParams: any): Observable<any> {
        const methodName = 'updateDevice';
        const url = this.getCallUrl(methodName);
        let params = {
            deviceUUID: deviceUUID
        };

        if (deviceParams) {
            params = Object.assign(params, deviceParams);
        }

        const config = this.getCallConfig(params, "application/json; charset=utf-8");

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    replaceRegCode(registrationCode: string, newRegistrationCode: string): Observable<any> {
        console.log("replaceRegCode: ", registrationCode, newRegistrationCode);
        const methodName = 'replaceRegCode';
        const url = this.getCallUrl(methodName);

        let params = {
            registrationCode: registrationCode,
            newRegistrationCode: newRegistrationCode
        };

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Update device
     * @param device Device data
     */
    // updateDevice(device: any): Observable<any> {
    //     const methodName = 'updateDevice';
    //     const url = this.getCallUrl(methodName, 'license');
    //
    //     console.log(methodName, url, device);
    //
    //     return this.http.post(url, device)
    //         .pipe(
    //             map(result => result),
    //             catchError(this.handleError)
    //         );
    // }

    /**
     * Get hidden columns
     */
    getHiddenColumns(): Observable<any> {
        const methodName = 'getHiddenColumns';
        const url = this.getCallUrl(methodName, 'license');
        const headers = this.getAuthHeaders();

        console.log(methodName, url, headers);

        return this.http.get(url, { headers })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Woo function (purpose unknown from original code)
     */
    woo(): Observable<any> {
        const methodName = 'woo';
        const url = this.getCallUrl(methodName, 'license');
        const params = {};

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getTStats(deviceUuid: string, body: string[], dateFrom: string, dateTo: string, limit: number,): Observable<any> {
        const methodName = 'getTStats';

        const url = this.getCallUrl(methodName);

        let params = { deviceUuid, dateFrom, dateTo, limit };

        const config = this.getCallConfig(params, 'application/json');

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, body, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    saveUserActivity(paramsUserClicked: any): Observable<any> {

        const methodName = 'saveUserActivity';

        const url = this.getCallUrl(methodName);

        const config = this.getCallConfig(paramsUserClicked);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Detach device
     * @param deviceUUID Device ID
     */
    detachDevice(deviceUUID: string): Observable<any> {
        const headers = this.getAuthHeaders();
        const methodName = 'detachDevice';
        const url = this.getCallUrl(methodName, 'backoffice');
        const params = { deviceUUID };

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params), headers })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get colors
     */
    colors(): Observable<any> {
        const methodName = 'colors';
        const url = this.getCallUrl(methodName, 'license');
        const params = {};

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get logo
     */
    getLogo(): Observable<any> {
        const methodName = 'getLogo';
        const url = this.getCallUrl(methodName, 'license');
        const params = {};

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get all device types
     */
    getAllDeviceTypes(): Observable<any> {
        const methodName = 'getAllDeviceTypes';
        const url = this.getCallUrl(methodName, 'license');
        const params = {};

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get full name
     */
    getFullName(): Observable<any> {
        const methodName = 'getFullName';
        const url = this.getCallUrl(methodName, 'license');
        const params = {};

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Download
     * @param deviceId Device ID
     */
    download(deviceId: string): Observable<any> {
        const methodName = 'download';
        const url = this.getCallUrl(methodName, 'license');
        const params = { deviceId };

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Download SIM
     * @param accountEmail
     * @param registrationCode
     * @param surname
     * @param type
     */
    downloadSIM(accountEmail: string, registrationCode?: string, surname?: string, type?: string): string {
        const methodName = "download";
        const url = this.getCallUrl(methodName);
        const myUrlWithParams = new URL(url);

        let params = {
            type: type,
        };

        if (accountEmail && accountEmail.length > 0) {
            params = Object.assign(params, { accountEmail });
        }
        if (registrationCode && registrationCode.length > 0) {
            params = Object.assign(params, { registrationCode });
        }
        if (surname && surname.length > 0) {
            params = Object.assign(params, { surname });
        }

        Object.entries(params).forEach(([key, val]) => {
            if (val !== null && val !== undefined) {
                myUrlWithParams.searchParams.append(key, String(val));
            }
        });
        return myUrlWithParams.href;
    }

    /**
     * refundDevice
     * @param deviceUuid
     * @param iccid
     */
    refundDevice(deviceUuid: string, iccid?: string): Observable<any> {
        const methodName = "refundDevice";
        const url = this.getCallUrl(methodName);
        let params = {
            deviceUuid: deviceUuid
        };

        if (iccid) {
            params = Object.assign(params, { iccid });
        }

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Download label
     * @param profileName
     * @param productName
     * @param deviceColor
     * @param registrationCode
     * @param msisdn
     */
    downloadLabel(profileName: string, productName: string, deviceColor: string, registrationCode: string, msisdn: string): string {
        const methodName = "download";
        const url = this.getCallUrl(methodName);
        const myUrlWithParams = new URL(url);
        let params = {
            type: "SIM_SHIPMENT_LABEL",
            profileName: profileName,
            productName: productName,
            deviceColor: deviceColor,
            registrationCode: registrationCode,
            msisdn: msisdn
        };

        Object.entries(params).forEach(([key, val]) => {
            if (val !== null && val !== undefined) {
                myUrlWithParams.searchParams.append(key, String(val));
            }
        });
        return myUrlWithParams.href;

        // return $http.get(url, config)
        //     .then(function (result) {
        //         return result.data;
        //     });
    }

    /**
     * Download SIM label
     * @param iccid ICCID
     */
    downloadSIMLabel(iccid: string): Observable<any> {
        const methodName = 'downloadSIMLabel';
        const url = this.getCallUrl(methodName, 'license');
        const params = { iccid };

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get account licenses
     * @param accountUUID Account ID
     */
    getAccountLicenses(accountUUID: string): Observable<any> {
        const methodName = 'getAccountLicenses';
        const url = this.getCallUrl(methodName, 'backoffice');
        const params = { accountUUID };

        console.log(methodName, url, params);

        var config = this.getCallConfig(params);

        console.log(url, params, config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Move license component
     * @param deviceUUID
     * @param destinationDeviceUUID
     * @param destinationLicenseUUID
     */
    moveLicenseComponent(deviceUUID: string, destinationDeviceUUID?: string, destinationLicenseUUID?: string): Observable<any> {
        const headers = this.getAuthHeaders();
        const methodName = 'moveLicenseComponent';
        const url = this.getCallUrl(methodName);
        let params = {
            deviceUUID
        };

        if (destinationDeviceUUID) {
            params = Object.assign(params, { destinationDeviceUUID });
        }
        if (destinationLicenseUUID) {
            params = Object.assign(params, { destinationLicenseUUID });
        }

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params), headers })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get payment methods for country
     * @param deviceType Device type
     * @param channel Channel
     * @param country Country
     */
    restrictedGetPaymentMethodsForCountry(deviceType: string, channel: string, country: string): Observable<any> {
        const methodName = 'restrictedGetPaymentMethodsForCountry';
        const url = this.getCallUrl(methodName, 'license');
        const params = {
            deviceType,
            channel,
            country
        };

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params) })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get payment method groups
     * @param userGroupId User group ID
     * @param productGroupId Product group ID
     * @param paymentChannelId Payment channel ID
     * @returns Observable of PaymentMethodGroup array
     */
    getPaymentMethodGroups(userGroupId: any, productGroupId: any, paymentChannelId: any): Observable<PaymentMethodGroup[]> {
        const headers = this.getAuthHeaders();
        const methodName = 'getPaymentMethodGroups';
        const context = 'paygw-clm/backoffice-www-1.0/config/';
        const url = this.getCallUrlBackOffice(methodName, context);

        const params: any = {
            productGroupId: productGroupId.id,
        };

        if (userGroupId !== undefined) {
            params.userGroupId = userGroupId.id;
        }

        if (paymentChannelId !== undefined) {
            params.paymentChannelId = paymentChannelId.id;
        }

        console.log(methodName, url, params);

        return this.http.get<PaymentMethodGroup[]>(url, { params: this.createHttpParams(params), headers })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get payment channels
     * @returns Observable of PaymentChannel array
     */
    getPaymentChannels(): Observable<PaymentChannel[]> {
        const headers = this.getAuthHeaders();
        const methodName = 'getPaymentChannels';
        const context = 'paygw-clm/backoffice-www-1.0/config/';
        const url = this.getCallUrlBackOffice(methodName, context);
        const params = {};

        console.log(methodName, url, params);

        return this.http.get<PaymentChannel[]>(url, { params: this.createHttpParams(params), headers })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get payment methods
     * @param paymentMethodGroupId Payment method group ID
     * @returns Observable of PaymentMethod array
     */
    getPaymentMethods(paymentMethodGroupId: string): Observable<PaymentMethod[]> {
        const headers = this.getAuthHeaders();
        const methodName = 'getPaymentMethods';
        const context = 'paygw-clm/backoffice-www-1.0/config/';
        const url = this.getCallUrlBackOffice(methodName, context);
        const params = { paymentMethodGroupId };

        console.log(methodName, url, params);

        return this.http.get<PaymentMethod[]>(url, { params: this.createHttpParams(params), headers })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    deletePaymentMethodGroup(paymentMethodGroupId: string): Observable<any> {
        const methodName = 'deletePaymentMethodGroup';
        const context = 'paygw-clm/backoffice-www-1.0/config/';
        const headers = this.getAuthHeaders();
        const url = this.getCallUrlBackOffice(methodName, context);
        let params = {
            paymentMethodGroupId: paymentMethodGroupId,
        };
        const config = this.getCallConfig(params);
        console.log("deletePaymentMethodGroup", url, params, config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : { headers }),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    createPaymentMethod(paymentMethodGroupId: string, method: string): Observable<any> {
        const methodName = 'createPaymentMethod';
        const context = 'paygw-clm/backoffice-www-1.0/config/';
        const headers = this.getAuthHeaders();
        const url = this.getCallUrlBackOffice(methodName, context);
        let params = {
            paymentMethodGroupId: paymentMethodGroupId,
        };
        const config = this.getCallConfig(params, 'application/json');
        console.log("createPaymentMethod", url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : { headers }),
        };

        return this.http.post(url, method, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    updatePaymentMethod(paymentMethodGroupId: string, paymentmethod: string): Observable<any> {
        const methodName = 'updatePaymentMethod';
        const context = 'paygw-clm/backoffice-www-1.0/config/';
        const headers = this.getAuthHeaders();
        const url = this.getCallUrlBackOffice(methodName, context);
        let params = {
            paymentMethodGroupId: paymentMethodGroupId,
        };
        const config = this.getCallConfig(params, 'application/json');
        console.log("updatePaymentMethod", url, paymentmethod, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : { headers }),
        };

        return this.http.post(url, paymentmethod, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    removePaymentMethod(paymentMethodId: string): Observable<any> {
        const methodName = 'removePaymentMethod';
        const context = 'paygw-clm/backoffice-www-1.0/config/';
        const headers = this.getAuthHeaders();
        const url = this.getCallUrlBackOffice(methodName, context);
        let params = {
            paymentMethodId: paymentMethodId,
        };
        const config = this.getCallConfig(params, 'application/json');
        console.log("removePaymentMethod", url, '', config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : { headers }),
        };

        return this.http.post(url, '', httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }


    createPaymentMethodGroup(productGroupId: string, userGroupId: string, paymentChannelId: string): Observable<any> {
        const methodName = 'createPaymentMethodGroup';
        const context = 'paygw-clm/backoffice-www-1.0/config/';
        const headers = this.getAuthHeaders();
        const url = this.getCallUrlBackOffice(methodName, context);
        let params = {
            productGroupId: productGroupId,
            userGroupId: userGroupId,
            paymentChannelId: paymentChannelId
        };
        const config = this.getCallConfig(params);
        console.log("createPaymentMethodGroup", url, params, config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : { headers }),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getUserGroups(): Observable<any> {
        const methodName = 'getUserGroups';
        const context = 'paygw-clm/backoffice-www-1.0/config/';
        const headers = this.getAuthHeaders();
        const url = this.getCallUrlBackOffice(methodName, context);
        let params = {};
        const config = this.getCallConfig(params);
        console.log("getUserGroups", url, params, config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : { headers }),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getProductGroups(): Observable<any> {
        const methodName = 'getProductGroups';
        const context = 'paygw-clm/backoffice-www-1.0/config/';
        const headers = this.getAuthHeaders();
        const url = this.getCallUrlBackOffice(methodName, context);
        let params = {};
        const config = this.getCallConfig(params);
        console.log("getProductGroups", url, params, config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : { headers }),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get device types
     * @returns Observable of device types
     */
    getDeviceTypes(): Observable<any> {
        const headers = this.getAuthHeaders();
        const methodName = 'getDeviceTypes';
        const url = this.getCallUrl(methodName, 'backoffice');
        const params = {};

        console.log(methodName, url, params);

        return this.http.get(url, { params: this.createHttpParams(params), headers })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getConfigSeriesList(deviceType: string): Observable<any> {
        var methodName = 'getConfigSeriesList';

        var url = this.getCallUrl(methodName);
        var params = {
            deviceType: deviceType
        };

        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getConfigurationSimList(deviceType: string, updateDateTime: number, iccid?: any, configurationType?: any): Observable<any> {
        var methodName = 'getConfigurationSimList';

        var url = this.getCallUrl(methodName);
        var params = {
            deviceType: deviceType,
            updateDateTime: updateDateTime,
        };

        if (iccid) {
            params = Object.assign(params, { iccid });
        }
        if (configurationType) {
            params = Object.assign(params, { configurationType });
        }

        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get device types for parental control
     * @returns Observable of device types for parental control
     */
    getDeviceTypesParental(): Observable<any> {
        const headers = this.getAuthHeaders();
        const methodName = 'getDeviceTypes';
        const url = this.getCallUrl(methodName, 'parctrl');
        const params = {};

        console.log(methodName, url, headers);

        return this.http.get(url, { headers, params })
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getExportProfileData(): Observable<any> {
        const methodName = 'exported_data/type/BO/list';
        const params = {};
        const config = this.getCallConfigAccount(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(this.getCallUrl(methodName, 'personal-data-service'), httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    exportProfileData(deviceId: string, controlDevices: boolean, chat: boolean, localization: boolean, stats: boolean): Observable<any> {
        const methodName = 'export';
        const params = {
            type: 'BO',
            deviceId: deviceId,
            instant: true,
            controlDevices: controlDevices,
            chat: chat,
            localization: localization,
            stats: stats
        };
        const config = this.getCallConfigAccount(params);

        console.log("config: ", config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(this.getCallUrl(methodName, 'personal-data-service'), httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    policeExportDownload(id: string): Observable<any> {
        const methodName = 'get_exported_data/' + id
        const params = {};
        const config = this.getCallConfigAccount(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
            responseType: 'arraybuffer' as const,
        };

        return this.http.get(this.getCallUrl(methodName, 'personal-data-service'), httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );

    }

    initReplaceDeviceLicense(sourceDeviceUuid: string, destinationRegistrationCode: string): Observable<any> {
        var methodName = 'initReplaceDeviceLicense';
        var url = this.getCallUrl(methodName);

        var params = {
            sourceDeviceUuid: sourceDeviceUuid,
            destinationRegistrationCode: destinationRegistrationCode
        };

        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'backoffice'), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    confirmReplaceDeviceLicense(sourceDeviceUuid: string, destinationRegistrationCode: string): Observable<any> {
        var methodName = 'confirmReplaceDeviceLicense';
        var url = this.getCallUrl(methodName);

        var params = {
            sourceDeviceUuid: sourceDeviceUuid,
            destinationRegistrationCode: destinationRegistrationCode
        };

        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'backoffice'), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    updateDeviceTypeFeatures(device: any): Observable<any> {
        const headers = this.getAuthHeaders();
        const methodName = 'updateDeviceTypeFeatures';
        const url = this.getCallUrl(methodName);
        const params = {
            deviceType: device.name
        };

        let config = this.getCallConfig(params, "application/json");

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : { headers }),
        };

        return this.http.post(url, JSON.stringify(device.features), httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    createMarkerFromLocation(localization: any, id: number, color: string) {
        if (!localization) {
            return null;
        }
        if (localization.latitude === null || localization.longitude === null || localization.latitude === 0 || localization.longitude === 0) {
            return null;
        }

        var marker = {
            id: id,
            latitude: localization.latitude,
            longitude: localization.longitude,
            coords: {
                latitude: localization.latitude,
                longitude: localization.longitude
            },
            visible: true,
            icon: this.createCanvasLastMarker(color),
            createTime: moment(localization.createTime).format('YYYY-MM-DD HH:mm:ss'),
            createTimeForDisplay: moment(localization.createTime).format('HH:mm'),
            address: '',
            info: "Lat:" + localization.latitude + " Long:" + localization.longitude,
            type: localization.localizationType,
            radius: 0,
            lastMarker: false,
            original: localization
        };
        if (localization.localizationType === 'LBS' && localization.properties && localization.properties.RADIUS) {
            marker.radius = localization.properties.RADIUS;
        }

        return marker;
    }

    getLastLocations(profileId: string, amount: number, deviceType: string, date: Moment): Observable<any> {
        if (!amount) {
            amount = 10;
        }
        return this.takeLastLocations(profileId, amount, deviceType, date).pipe(
            map((resp: any) => {
                const lastLocations: any[] = [];
                if (resp && Array.isArray(resp.localizations)) {
                    resp.localizations.forEach((localization: any, index: number) => {
                        const viewLocation = this.createMarkerFromLocation(localization, index, '');
                        if (viewLocation && lastLocations.indexOf(viewLocation) === -1) {
                            lastLocations.push(viewLocation);
                        }
                    });
                }
                return lastLocations;
            })
        );
    }

    takeLastLocations(deviceId: string, amountLocations: number, deviceType: string, date: Moment): Observable<any> {
        const methodName = 'takeLastLocations';
        let dateFrom = null;
        let dateTo = null;

        if (null !== date) {
            dateFrom = moment(date);
            dateFrom = dateFrom.hours(0).minutes(0).seconds(0).milliseconds(0)
                .format("YYYY-MM-DD[T]HH:mm:ssZ");
            dateTo = moment(date);
            dateTo = dateTo.hours(23).minutes(59).seconds(59).milliseconds(999)
                .format("YYYY-MM-DD[T]HH:mm:ssZ");
        }

        const currentDateTo = false;

        const params = {
            deviceId: deviceId,
            deviceType: deviceType,
            amountLocations: amountLocations,
            dateFrom: dateFrom,
            dateTo: dateTo,
            lbsTimeFactor: 0,
            currentDateTo: currentDateTo
        };

        const config = this.getCallConfigAccount(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'parctrl'), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );

    }

    getSimTotalBalance(email: string, deviceId: string): Observable<any> {
        var methodName = 'getSimTotalBalance';
        var context = 'paygw-clm/backoffice-www-1.0/';
        var params = {
            deviceId: deviceId,
            email: email
        };
        const headers = this.getAuthHeaders();
        console.log('email ' + email + ' deviceId ' + deviceId);
        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : { headers }),
        };

        return this.http.post(this.getCallUrlBackOffice(methodName, context), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getLocalizations(accountUUID: string, deviceId: string, fromDate: string, toDate: string, size: number): Observable<any> {
        var methodName = 'getLocalizations';

        var params = {
            'deviceId': deviceId,
            'accountUuid': accountUUID,
            'fromDate': fromDate,
            'toDate': toDate,
            'size': size
        };
        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'backoffice'), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    nativeSubscriptionStatus(platform: string, subscriptionId: string): Observable<any> {
        var methodName = 'nativeSubscriptionStatus';
        var params = {
            'platform': platform,
            'subscriptionId': subscriptionId
        };
        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'backoffice'), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    paymentUnsubscribe(platform: string, subscriptionId: string): Observable<any> {
        var methodName = 'paymentUnsubscribe';
        var params = {
            'platform': platform,
            'subscriptionId': subscriptionId
        };
        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'backoffice'), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }


    getConfiguration(deviceId: string): Observable<any> {
        const methodName = 'getConfiguration';

        const params = {
            'deviceId': deviceId,
        };

        const config = this.getCallConfigAccount(params, 'application/json');  //  service.token

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'parctrl'), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );

    }

    getAppUsageStats(deviceId: string, date: Moment): Observable<any> {
        const methodName = 'getAppUsageStats';
        let dateFrom = moment(date).subtract(7, 'days');
        let dateFromString = dateFrom.hours(0).minutes(0).seconds(0).milliseconds(0)
            .format("YYYY-MM-DD[T]HH:mm:ssZ");
        let dateTo = moment(date);
        let dateToString = dateTo.hours(23).minutes(59).seconds(59).milliseconds(999)
            .format("YYYY-MM-DD[T]HH:mm:ssZ");
        const params = {
            deviceId: deviceId,
            dateFrom: dateFromString,
            dateTo: dateToString
        };

        const config = this.getCallConfigAccount(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'parctrl'), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getConfigurationElements(deviceId: string, elements: string[]): Observable<any> {
        const methodName = 'getConfigurationElements';
        const params = {
            deviceId: deviceId
        };

        const config = this.getCallConfigAccount(params, 'application/json');

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'parctrl2'), elements, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getDeviceStatusParams(deviceId: string): Observable<any> {
        const methodName = 'getDeviceStatusParams';
        const params = {
            deviceId: deviceId
        };

        const config = this.getCallConfigAccount(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'parctrl'), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getNoLocationDates(deviceId: string): Observable<any> {
        const methodName = 'getNoLocationDates';
        const params = {
            deviceId: deviceId
        };
        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'parctrl'), null, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    isDeviceActive(deviceId: string): Observable<any> {
        const methodName = 'isDeviceActive';
        const params = {
            deviceId: deviceId
        };
        const config = this.getCallConfigAccount(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'parctrl'), null, httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );
    }

    getLicenseList(): Observable<any> {
        const methodName = 'getList';
        const params = {
            dateFrom: moment().year(2010).format("YYYY-MM-DD[T]HH:mm:ssZ"),
            dateTo: moment().year(2050).format("YYYY-MM-DD[T]HH:mm:ssZ")
        };
        const config = this.getCallConfigAccount(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'license'), '', httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );
    }

    getStats(clientDeviceId: string, start: number, end: number): Observable<any> {
        let methodName = 'getStats';

        let params = {
            'dateFrom': moment().subtract(30, 'days').format("YYYY-MM-DD[T]HH:mm:ssZ"),
            'dateTo': moment().format("YYYY-MM-DD[T]HH:mm:ssZ"),
            'rowFrom': start,
            'rowTo': end,
            'order': 'desc',
            'deviceId': clientDeviceId
        };
        const config = this.getCallConfigAccount(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, 'parctrl'), '', httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );
    }


    allAccessLogin(accountEmail: string, tenant?: string): Observable<any> {
        const methodName = 'allAccessLogin';
        let params;
        if (tenant && tenant === 'Locon') {
            params = {
                accountEmail: accountEmail,
                consumerKey: this.calmeanAuthService.getConsumerLoconKey(),
                consumerSecret: this.calmeanAuthService.getConsumerLoconSecret(),
                tenant: tenant
            };
        } else {
            params = {
                accountEmail: accountEmail,
                consumerKey: this.calmeanAuthService.getConsumerKey(),
                consumerSecret: this.calmeanAuthService.getConsumerSecret(),
            };
        }
        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName, "account"), null, httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );
    }

    getMethodLoginAccessCode(tenant: string, accountEmail: string): Observable<any> {
        console.log('getMethodLoginAccessCode');
        const methodName = 'getMethodLoginAccessCode';
        const params = {
            tenant: tenant,
            email: accountEmail
        };
        const url = this.getCallUrl(methodName, undefined);
        const config = this.getCallConfig(params);

        console.log(url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    createLoginAccessCode(timeToLive: number, accountEmail: string, type: string): Observable<any> {
        console.log('createLoginAccessCode');
        const methodName = 'createLoginAccessCode';
        const context = "account";
        const params = {
            timeToLive: timeToLive * 1000,
            accountEmail: accountEmail,
            type: type
        };
        const url = this.getCallUrl(methodName, context, false, 'https://api2.calmean.com');
        const config = this.getCallConfig(params);

        console.log(url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    moveAccount(sourceEmail: string, destinationEmail: string): Observable<any> {
        const methodName = 'moveAccount';
        const url = this.getCallUrl(methodName, 'account');
        const params = { sourceEmail, destinationEmail };
        return this.http.post<any>(url, params, { headers: this.getAuthHeaders() })
            .pipe(
                catchError(this.handleError)
            );
    }

    erase(accessToken: string): Observable<any> {
        const methodName = 'erase';
        const url = this.getCallUrl(methodName, 'personal-data-service');
        let config = this.getCallConfig({}, 'application/x-www-form-urlencoded', accessToken);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );
    }

    // erase1(token) {
    //     var methodName = 'erase';
    //     var params = {};
    //     var config = getCallConfig(params, 'application/x-www-form-urlencoded', token);
    //
    //     var promise = $http.get(getCallUrl(methodName, 'personal-data-service'), config)
    //         .then(function (response) {
    //
    //             return response.data;
    //         }).catch(function (response) {
    //             var message = 'An error occurred';
    //             if (response.data && response.data.message) {
    //                 message = response.data.message;
    //             }
    //             return $q.reject(message);
    //         });
    //     return promise;
    // }

    getLicenseTypeNames(licensePaymentGroup: string): Observable<any> {
        const methodName = "getLicenseTypeNames";
        const url = this.getCallUrl(methodName);
        const params = {
            licensePaymentGroup: licensePaymentGroup
        };

        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    updateLicense(licenseUUID: string, paymentGroup: string, licenseTypeName: string): Observable<any> {
        const methodName = "updateLicense";
        const url = this.getCallUrl(methodName);
        const params = {
            licensePaymentGroup: paymentGroup,
            licenseType: licenseTypeName,
            licenseUUID: licenseUUID
        };

        const config = this.getCallConfig(params);

        console.log(url, params, config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    validatePhoneNumber(deviceId: string, msisdn: string, countryCode: string): Observable<any> {
        const methodName = 'validatePhoneNumber';

        const params = {
            deviceId: deviceId,
            msisdn: msisdn,
            country: countryCode
        };
        const config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        // Use {} as the POST body if no payload is required
        return this.http.post(this.getCallUrl(methodName), {}, httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );
    }

    notifyConfigurationUpdate(deviceUUID: string): Observable<any> {
        var methodName = 'notifyConfigurationUpdate';
        var url = this.getCallUrl(methodName);
        var params = {
            deviceUUID: deviceUUID
        };

        var config = this.getCallConfig(params, "application/json; charset=utf-8");

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName), {}, httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );
    }

    getOrderSource4Device(deviceId: string): Observable<any> {
        var url = this.getCallUrl('getOrderSource4Device');
        var config = this.getCallConfig({ deviceId: deviceId });
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.get(url, httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );
    }

    sendPhoneBookMsisdn(deviceUuid: string, msisdn: string): Observable<any> {
        var methodName = "sendPhoneBookMsisdn";
        var url = this.getCallUrl(methodName);
        var params = {
            deviceUuid: deviceUuid,
            msisdn: msisdn
        };

        var config = this.getCallConfig(params);

        console.log(url, params, config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    shutdownDevice(deviceUuid: string): Observable<any> {
        var methodName = 'shutdownDevice';
        var url = this.getCallUrl(methodName);
        var params = {
            deviceUuid: deviceUuid,
        };
        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName), {}, httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );
    }

    updateLicenseValidityDates(licenseUUID: string, validTo: string, validFrom?: string, extendedValidity?: number): Observable<any> {
        const methodName = "updateLicenseValidityDates";
        const url = this.getCallUrl(methodName);
        let params = {
            validTo: validTo,
            licenseUUID: licenseUUID
        };
        if (validFrom) {
            params = Object.assign(params, { validFrom });
        }
        if (extendedValidity) {
            params = Object.assign(params, { extendedValidity });
        } else {
            params = Object.assign(params, { extendedValidity: 0 });
        }

        var config = this.getCallConfig(params);

        console.log(url, params, config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    updateLicenseFreemiumValidityDate(licenseUUID: string, freemiumValidTo: string): Observable<any> {
        const methodName = "updateLicenseValidityDates";
        const url = this.getCallUrl(methodName);
        const params = {
            freemiumValidTo: freemiumValidTo,
            licenseUUID: licenseUUID
        };
        const config = this.getCallConfig(params);

        console.log(url, params, config);
        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getDeviceLicense(accountUUID: string): Observable<any> {
        const methodName = "getDeviceLicense";
        const url = this.getCallUrl(methodName);
        const params = {
            deviceUUID: accountUUID
        };
        const config = this.getCallConfig(params);

        console.log(url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    getCallConfig(params: any, contentType?: string, _token?: string) {
        let token = this.authService.getCalmeanToken();
        if (_token)
            token = _token;

        if (!token) {
            console.warn('No Calmean token found. API requests may fail with 401 Unauthorized.');
            return new HttpHeaders();
        }

        if (!contentType) {
            contentType = 'application/x-www-form-urlencoded';
        }

        const headers = {
            'Authorization': 'Bearer ' + token,
            'Content-Type': contentType
        };

        return {
            headers: headers,
            params: params
        };
    }

    getCallConfigAccount(params: any, contentType?: string) {
        const token = this.authService.getAccountToken();
        if (!token) {
            console.warn('No Calmean token found. API requests may fail with 401 Unauthorized.');
            return new HttpHeaders();
        }

        if (!contentType) {
            contentType = 'application/x-www-form-urlencoded';
        }

        const headers = {
            'Authorization': 'Bearer ' + token,
            'Content-Type': contentType
        };

        return {
            headers: headers,
            params: params
        };
    }

    // Helper methods

    /**
     * Get call URL
     * @param methodName Method name
     * @param context Context (optional)
     * @param addApi Whether to add 'api/' to the URL (optional)
     * @param alternativeHost Alternative host to use (optional)
     * @returns URL string
     */
    private getCallUrl(methodName: string, context?: string, addApi?: boolean, alternativeHost?: string): string {
        // Determine context based on method name if not provided
        if (context === undefined) {
            context = 'backoffice';
            if (methodName === 'getEmail' || methodName === 'setEmail' ||
                methodName === 'alert' || methodName === 'restrictedGetLocalizations') {
                context = 'parctrl';
            }
            if (methodName === 'getAccount' || methodName === 'restrictedGetAccount') {
                context = 'account';
            }
            if (methodName === 'validatePhoneNumber' || methodName === 'getOrderSource4Device') {
                context = 'parctrl2';
            }
            if (methodName === 'convert' || methodName === 'messageById') {
                context = 'mail-service';
            }
        }

        // Set default value for addApi
        if (addApi === undefined) {
            addApi = false;
        }

        // Set default value for alternativeHost
        if (alternativeHost === undefined) {
            alternativeHost = environment.apiUrl;
            if (methodName === 'erase') {
                // In the original JS, hostWithoutPort is used for 'erase' method
                // But both host and hostWithoutPort are the same value
                alternativeHost = environment.apiUrl;
            }
        }

        // Determine URL end based on addApi flag
        const urlEnd = addApi ? 'api/' + methodName : methodName;

        // Construct and return the URL
        // Original format: alternativeHost + 't/calmean.com/' + context + '/1.0.0/' + urlEnd
        return `${alternativeHost}/t/calmean.com/${context}/1.0.0/${urlEnd}`;
    }

    /**
     * Get back office call URL
     * @param methodName Method name
     * @param context Context
     * @returns URL string
     */
    private getCallUrlBackOffice(methodName: string, context: string): string {
        // Original format: host + 't/calmean.com/' + context + urlEnd
        // where urlEnd is just methodName
        return `${environment.apiUrl}/t/calmean.com/${context}${methodName}`;
    }

    /**
     * Create HTTP options for GET/POST requests
     * @param params Query params (for GET)
     * @param contentType Content-Type header value
     * @param token Optional token override
     * @returns Object with headers and (optionally) params
     */
    // private createHttpOptions(params?: any, contentType: string = 'application/json', token?: string): any {
    //     const options: any = { headers: this.getAuthHeaders(contentType, token) };
    //     if (params) {
    //         options.params = this.createHttpParams(params);
    //     }
    //     return options;
    // }

    /**
     * Create HTTP params from object
     * @param params Parameters object
     */
    private createHttpParams(params: any): HttpParams {
        let httpParams = new HttpParams();

        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                httpParams = httpParams.append(key, params[key]);
            }
        }

        return httpParams;
    }

    /**
     * Get HTTP headers with authentication token
     * @param contentType Content-Type header value (default: application/json)
     * @param token Optional token override
     * @returns HttpHeaders with Authorization and Content-Type
     */
    /**
     * Get HTTP headers with authentication token
     * @returns HttpHeaders with Authorization header
     */
    private getAuthHeaders(): HttpHeaders {
        const token = this.authService.getCalmeanToken();
        if (!token) {
            console.warn('No Calmean token found. API requests may fail with 401 Unauthorized.');
            return new HttpHeaders();
        }
        return new HttpHeaders({
            'Authorization': `Bearer ${token}`
        });
    }

    /**
     * Store data in session storage
     * @param key Key
     * @param value Value
     */
    setSessionData(key: string, value: any): void {
        try {
            window.sessionStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
            console.error('Error saving to sessionStorage', e);
        }
    }

    /**
     * Get data from session storage
     * @param key Key
     * @returns Stored value or null if not found
     */
    getSessionData<T>(key: string): T | null {
        try {
            const item = window.sessionStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } catch (e) {
            console.error('Error reading from sessionStorage', e);
            return null;
        }
    }

    /**
     * Remove data from session storage
     * @param key Key
     */
    removeSessionData(key: string): void {
        try {
            window.sessionStorage.removeItem(key);
        } catch (e) {
            console.error('Error removing from sessionStorage', e);
        }
    }

    /**
     * Error handler for HTTP requests
     */
    private handleError(error: HttpErrorResponse) {
        console.error('API error occurred:', error);

        let errorMessage = 'An unknown error occurred';
        if (error.error instanceof ErrorEvent) {
            // Client-side error
            errorMessage = `Error: ${error.error.message}`;
        } else {
            // Server-side error
            errorMessage = error.error?.message || `Error Code: ${error.status}, Message: ${error.message}`;
        }

        return throwError(() => new Error(errorMessage));
    }

    /**
     * Get payment history for an account
     * @param email Account email
     * @returns Observable with payment history data
     */
    getPaymentHistory(email: string): Observable<any> {
        const methodName = 'getPaymentHistory';
        const context = 'paygw-clm/backoffice-www-1.0/';

        const url = this.getCallUrlBackOffice(methodName, context);
        const params = { email };

        return this.http.post<any>(url, params, { headers: this.getAuthHeaders() })
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Update PayPal subscription status
     * @param profileId PayPal profile ID
     * @param status New status
     * @returns Observable with update result
     */
    updatePaypalStatus(profileId: string, status: string): Observable<any> {
        const methodName = 'updatePaypalStatus';
        const context = 'paygw-clm/backoffice-www-1.0/';

        const url = this.getCallUrlBackOffice(methodName, context);
        const params = { profileId, status };

        const headers = this.getAuthHeaders();

        let config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : { headers }),
        };

        return this.http.post<any>(url, params, httpOptions)
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Deactivate Direct Billing subscription
     * @param transactionId Transaction ID
     * @returns Observable with deactivation result
     */
    deactivateDB(transactionId: string): Observable<any> {
        const methodName = 'deactivateDB';
        const context = 'paygw-clm/backoffice-www-1.0/';

        const url = this.getCallUrlBackOffice(methodName, context);
        const params = { transactionId };

        return this.http.post<any>(url, params, { headers: this.getAuthHeaders() })
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Get device history
     * @param deviceUuid Device UUID
     * @param page Page number (0-based)
     * @param size Page size
     * @returns Observable with device history data
     */
    getDeviceHistory(deviceUuid: string, page: number, size: number): Observable<any> {
        const methodName = 'getDeviceHistory';
        const url = this.getCallUrl(methodName);
        const params = {
            deviceUUID: deviceUuid,
            page: page,
            size: size
        };

        return this.http.get<any>(url, {
            params: this.createHttpParams(params),
            headers: this.getAuthHeaders()
        }).pipe(
            catchError(this.handleError)
        );
    }

    /**
     * Get device configuration SMS messages
     * @param deviceUuid Device UUID
     * @returns Observable with device configuration SMS messages
     */
    getDeviceConfigurationSmsMessages(deviceUuid: string): Observable<any> {
        const methodName = 'getDeviceConfigurationSmsMessages';
        const url = this.getCallUrl(methodName);
        const params = {
            deviceUuid: deviceUuid
        };

        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(this.getCallUrl(methodName), {}, httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );
    }

    /**
     * Get keep alive activity for a device
     * @param deviceUUID Device UUID
     * @param fromDate From date
     * @param toDate To date
     * @returns Observable with keep alive activity data
     */
    getKeepAliveActivity(deviceUUID: string, fromDate: Date, toDate: Date): Observable<any> {
        const methodName = 'getKeepAliveActivity';
        const url = this.getCallUrl(methodName);
        const params = {
            deviceUuid: deviceUUID,
            fromDate: moment(fromDate).format("YYYY-MM-DD[T]HH:mm:ssZ"),
            toDate: moment(toDate).format("YYYY-MM-DD[T]HH:mm:ssZ")
        };

        var config = this.getCallConfig(params);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post(url, params, httpOptions)
            .pipe(
                map((data) => data),
                catchError(this.handleError)
            );

        // return this.http.post<any>(url, params, {headers: this.getAuthHeaders()}).pipe(
        //     catchError(this.handleError)
        // );
    }

    /**
     * Fetch WiFi-based groups for repairing GPS sleep localizations.
     * Mirrors legacy CalmeanDataService.getGPSSleepLocalizations(accountUuid, deviceId, fromDate, toDate)
     */
    getGPSSleepLocalizations(accountUuid: string, deviceId: number | string, fromDate: Date, toDate: Date): Observable<any> {
        const methodName = 'getGPSSleepLocalizations';
        const url = this.getCallUrl(methodName);
        const params: any = {
            accountUuid: accountUuid,
            deviceId: deviceId,
            fromDate: moment(fromDate).format("YYYY-MM-DD[T]HH:mm:ssZ"),
            toDate: moment(toDate).format("YYYY-MM-DD[T]HH:mm:ssZ")
        };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.post<any>(url, params, httpOptions).pipe(
            map(result => result),
            catchError(this.handleError)
        );
    }

    /**
     * Persist selected WiFi MAC addresses as fixed GPS sleep localization anchors
     * Mirrors legacy CalmeanDataService.setGPSSleepLocalizations(accountUUID, deviceId, macSet)
     */
    setGPSSleepLocalizations(accountUuid: string, deviceId: number | string, macSet: string[]): Observable<any> {
        const methodName = 'setGPSSleepLocalizations';
        const url = this.getCallUrl(methodName);
        const params: any = {
            accountUuid: accountUuid,
            deviceId: deviceId,
            macSet: macSet
        };
        const config: any = this.getCallConfig(params, "application/json");
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.post<any>(url, params, httpOptions).pipe(
            map(result => result),
            catchError(this.handleError)
        );
    }

    /**
     * Fetch list of configured GPS Sleep rules for the device
     * Mirrors legacy CalmeanDataService.getGPSSleepConfigList(accountUUID, deviceId)
     */
    getGPSSleepConfigList(accountUuid: string, deviceId: number | string): Observable<any> {
        const methodName = 'getGPSSleepConfigList';
        const url = this.getCallUrl(methodName);
        const params: any = {
            accountUuid: accountUuid,
            deviceId: deviceId
        };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.post<any>(url, params, httpOptions).pipe(
            map(result => result),
            catchError(this.handleError)
        );
    }

    /**
     * Delete a GPS Sleep configuration by id for a device
     * Mirrors legacy CalmeanDataService.deleteGPSSleepConfig(accountUUID, deviceId, configId)
     */
    deleteGPSSleepConfig(accountUuid: string, deviceId: number | string, configId: number | string): Observable<any> {
        const methodName = 'deleteGPSSleepConfig';
        const url = this.getCallUrl(methodName);
        const params: any = {
            accountUuid: accountUuid,
            deviceId: deviceId,
            configId: configId
        };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.post<any>(url, params, httpOptions).pipe(
            map(result => result),
            catchError(this.handleError)
        );
    }

    /**
     * Get T-Mobile shipping address and processing status for a device
     * Mirrors legacy getAccountNotificationShippingAddress(deviceUUID)
     */
    getAccountNotificationShippingAddress(deviceUUID: string): Observable<any> {
        const methodName = 'getAccountNotificationShippingAddress';
        const url = this.getCallUrl(methodName);
        const params = { deviceUUID };
        const config = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        // Some endpoints expect POST even for reads in this backend; match existing pattern
        return this.http.post<any>(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Set T-Mobile shipping address for a device
     * Mirrors legacy setAccountNotificationShippingAddress(deviceUUID, shippingAddressDTO)
     */
    setAccountNotificationShippingAddress(deviceUUID: string, shippingAddressDTO: { name: string; company: string; address: string; postCode: string; city: string; }): Observable<any> {
        const methodName = 'setAccountNotificationShippingAddress';
        const url = this.getCallUrl(methodName);
        const params: any = {
            deviceUUID,
            ...shippingAddressDTO
        };
        const config = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.post<any>(url, params, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Get notification message history for a device/account
     * Mirrors legacy CalmeanDataService.getMessageHistory(accountUUID, deviceId, fromDate, toDate, limit, typeMsg, typeRecipient)
     */
    getMessageHistory(accountUuid: string, deviceId: string | undefined, fromDate: Date, toDate: Date, limit: number,
        typeMsg?: string | null, typeRecipient?: string | null): Observable<any> {
        const methodName = 'getMessageHistory';
        const url = this.getCallUrl(methodName);
        const params: any = {
            accountUuid: accountUuid,
            fromDate: moment(fromDate).format("YYYY-MM-DD[T]HH:mm:ssZ"),
            toDate: moment(toDate).format("YYYY-MM-DD[T]HH:mm:ssZ"),
            limit: limit
        };
        if (deviceId) {
            params.deviceId = deviceId;
        }
        if (typeMsg) {
            params.type = typeMsg;
        }
        if (typeRecipient) {
            params.recipientType = typeRecipient;
        }
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Fetch mail message template meta by id (for screenshots/campaign id)
     */
    messageById(templateId: string): Observable<any> {
        const methodName = 'messageById';
        const url = this.getCallUrl(methodName); // context auto-switches to 'mail-service'
        const params: any = { id: templateId };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Get control (parent) devices for account
     * Mirrors legacy CalmeanDataService.getControlDevices(accountUuid)
     */
    getControlDevices(accountUuid: string): Observable<any> {
        const methodName = 'getControlDevices';
        const url = this.getCallUrl(methodName);
        const params: any = { accountUuid };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        // Legacy used POST for list endpoints
        return this.http.post<any>(url, params, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Send test push to a device's parents
     * Mirrors legacy CalmeanDataService.pushTest(deviceUuid)
     */
    pushTest(deviceUuid: string): Observable<any> {
        const methodName = 'pushTest';
        const url = this.getCallUrl(methodName);
        const params: any = { deviceUuid };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Send Locon push command (mirror of legacy CalmeanDataService.sendPush)
     * Endpoint: context 'bobaselinker', api/send_locon_push
     */
    sendPush(command: string, gcmId: string, duration: number): Observable<any> {
        const methodName = 'send_locon_push';
        const url = this.getCallUrl(methodName, 'bobaselinker', true);
        const params: any = {};
        const body = {
            gcmId: gcmId,
            duration: String(duration),
            command: command
        };

        const config: any = this.getCallConfig(params, 'application/json; charset=utf-8');
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.post<any>(url, body, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Search accounts by email with pagination
     * Mirrors legacy CalmeanDataService.getAccounts(page, size, accountEmail)
     */
    getAccounts(page: number, size: number, accountEmail: string): Observable<any> {
        const methodName = 'getAccounts';
        const url = this.getCallUrl(methodName); // default context 'backoffice'
        const params: any = { page, size, accountEmail };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Generate change password link for an account
     * Mirrors legacy CalmeanDataService.createChangePasswdLink(email)
     */
    createChangePasswdLink(email: string): Observable<any> {
        const methodName = 'createChangePasswdLink';
        const url = this.getCallUrl(methodName); // keep 'backoffice' context
        const params: any = { email };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        // Backend typically uses POST for actions
        return this.http.post<any>(url, params, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Upload MSISDN pack content (file or text)
     * Mirrors legacy CalmeanDataService.uploadContent(fd, key, type)
     */
    uploadContent(body: any, key: string, contentType?: string): Observable<any> {
        const methodName = `uploadMsisdnPack/${key}`;
        const url = this.getCallUrl(methodName, 'devicegw');
        let headers = this.getAuthHeaders();
        if (contentType) {
            headers = headers.set('Content-Type', contentType);
        }
        const httpOptions: any = { headers };
        return this.http.post<any>(url, body, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Get device gateway pack list (paginated)
     * Mirrors legacy CalmeanDataService.getPackListDeviceGw(page, size)
     */
    getPackListDeviceGw(page: number, size: number): Observable<any> {
        const methodName = 'getPackList';
        const url = this.getCallUrl(methodName, 'devicegw');
        const params: any = { page, size };
        const httpOptions: any = {
            headers: this.getAuthHeaders(),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Update a pack visibility/order
     * Mirrors legacy CalmeanDataService.updatePack(packName, visible, order)
     */
    updatePack(packName: string, visible: boolean, order: number): Observable<any> {
        const methodName = 'updatePack';
        const url = this.getCallUrl(methodName, 'devicegw');
        const params: any = { packName, visible, order };
        const httpOptions: any = {
            headers: this.getAuthHeaders(),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Get MSISDN list for a pack name
     * Mirrors legacy CalmeanDataService.getPackListMsisdnList(packName)
     */
    getPackListMsisdnList(packName: string): Observable<any> {
        const methodName = 'getPackMsisdnList';
        const url = this.getCallUrl(methodName, 'devicegw',);
        const params: any = { packName };
        const httpOptions: any = {
            headers: this.getAuthHeaders(),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Promo Codes: get pack list
     * Mirrors legacy CalmeanDataService.getPackList(page, size)
     */
    getPromoPackList(pageNumber: number, pageSize: number): Observable<any> {
        const methodName = 'api/getPackList';
        const url = this.getCallUrl(methodName, 'calmean-codes');
        const params: any = { pageNumber, pageSize };
        const httpOptions: any = {
            headers: this.getAuthHeaders(),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Promo Codes: get downloadId for PDF of a pack
     * Mirrors legacy CalmeanDataService.getDownloadIdPackPdf(id)
     */
    getPromoPackDownloadIdPdf(packId: number | string): Observable<any> {
        const methodName = 'api/getDownloadIdPackPdf';
        const url = this.getCallUrl(methodName, 'calmean-codes');
        const params: any = { packId };
        const httpOptions: any = {
            headers: this.getAuthHeaders(),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Promo Codes: get pack content by id (for CSV)
     * Mirrors legacy CalmeanDataService.getDownloadPackById(id)
     */
    getPromoPackById(packId: number | string): Observable<any> {
        const methodName = 'api/getDownloadPackById';
        const url = this.getCallUrl(methodName, 'calmean-codes');
        const params: any = { packId };
        const httpOptions: any = {
            headers: this.getAuthHeaders(),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Promo Codes: get single code info by code value
     * Mirrors legacy CalmeanDataService.getCode(code)
     */
    getPromoCode(codeValue: string): Observable<any> {
        const methodName = 'api/getCode';
        const url = this.getCallUrl(methodName, 'calmean-codes');
        const params: any = { codeValue };
        const httpOptions: any = {
            headers: this.getAuthHeaders(),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Promo Codes: get available products
     * Mirrors legacy CalmeanDataService.getProductList()
     */
    getPromoProductList(): Observable<any> {
        const methodName = 'api/getProductList';
        const url = this.getCallUrl(methodName, 'calmean-codes');
        const httpOptions: any = { headers: this.getAuthHeaders() };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Promo Codes: generate new pack
     * Mirrors legacy CalmeanDataService.generateCodes(name, view, product, packSize, productQuantity, validityDate)
     */
    generatePromoCodes(name: string, view: string, product: string, packSize: number, productQuantity: number, validityDate: Date): Observable<any> {
        const methodName = 'generateCodes';
        const url = this.getCallUrl(methodName, 'calmean-codes');
        const params: any = {
            name,
            view,
            product,
            packSize,
            validityDate: validityDate instanceof Date ? validityDate.toISOString() : validityDate,
            productQuantity
        };
        const httpOptions: any = {
            headers: this.getAuthHeaders(),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    generateCodes(name: string, view: string, product: string, packSize: number, productQuantity: number, validityDate: string): Observable<any> {
        var methodName = "generateCodes";
        var context = "calmean-codes";
        var url = this.getCallUrl(methodName, context, true);
        var params = {
            name: name,
            view: view,
            product: product,
            packSize: packSize,
            validityDate: validityDate,
            productQuantity: productQuantity
        };

        var config = this.getCallConfig(params);

        console.log(url, params, config);

        const httpOptions = {
            ...(config && typeof config === 'object' ? config : {}),
        };

        return this.http.get(url, httpOptions)
            .pipe(
                map(result => result),
                catchError(this.handleError)
            );
    }

    /**
     * Tasks list from Backoffice (mirrors legacy getTaskList)
     */
    getTaskList(type: string, page: number, pageSize: number): Observable<any> {
        const methodName = 'getTaskList';
        const url = this.getCallUrl(methodName);
        const params: any = { type, page, pageSize };
        const httpOptions: any = {
            headers: this.getAuthHeaders(),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Create device configuration labels PDF task (mirrors legacy createConfigDeviceLabelPdf)
     */
    createConfigDeviceLabelPdf(deviceType: string, colour: string, languageSupport: string, amount: number, simStatusCode: string, series: string): Observable<any> {
        const methodName = 'createConfigDeviceLabelPdf';
        const url = this.getCallUrl(methodName);
        const params: any = { deviceType, colour, languageSupport, amount, simStatusCode, series };
        const httpOptions: any = {
            headers: this.getAuthHeaders(),
            params: this.createHttpParams(params)
        };
        return this.http.get<any>(url, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Get geo fail locations (mirrors legacy CalmeanDataService.getGeoFailLocations)
     */
    getGeoFailLocations(accountUuid: string, deviceId: string | number, fromDate: Date | string, toDate: Date | string): Observable<any> {
        const methodName = 'getGeoFailLocations';
        const url = this.getCallUrl(methodName);
        const params: any = {
            accountUuid,
            deviceId,
            fromDate: moment(fromDate).format("YYYY-MM-DD[T]HH:mm:ssZ"),
            toDate: moment(toDate).format("YYYY-MM-DD[T]HH:mm:ssZ")
        };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.post<any>(url, params, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Set geo fix location (mirrors legacy CalmeanDataService.setGeoFixLocation)
     */
    setGeoFixLocation(id: number | string, latitude: number, longitude: number, radius: number, skip: boolean): Observable<any> {
        const methodName = 'setGeoFixLocation';
        const url = this.getCallUrl(methodName);
        const params = { id, latitude, longitude, radius, skip };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.post<any>(url, params, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Get localization reports (mirrors legacy CalmeanDataService.getLocalizationReports)
     */
    getLocalizationReports(deviceUuid: string): Observable<any> {
        const methodName = 'getLocalizationReports';
        const url = this.getCallUrl(methodName);
        const params = { deviceUuid };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.post<any>(url, params, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    /**
     * Set localization report geo store (mirrors legacy CalmeanDataService.setLocalizationReportGeoStore)
     */
    setLocalizationReportGeoStore(id: number | string, enabled: boolean): Observable<any> {
        const methodName = 'setLocalizationReportGeoStore';
        const url = this.getCallUrl(methodName);
        const params = { id, enabled };
        const config: any = this.getCallConfig(params);
        const httpOptions: any = {
            ...(config && typeof config === 'object' ? config : {}),
        };
        return this.http.post<any>(url, params, httpOptions)
            .pipe(map(result => result), catchError(this.handleError));
    }

    locationsMapSettings: any = {};

}
